import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import usuarioApi from '../api/usuarioApi';

const useUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener todos los usuarios
  const fetchUsuarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await usuarioApi.getAll();
      // Asegurar que siempre sea un array
      const usuariosData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data && Array.isArray(response.data.data))
          ? response.data.data
          : [];
      setUsuarios(usuariosData);
      return usuariosData;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error al cargar usuarios';
      setError(errorMessage);
      toast.error(errorMessage);
      // En caso de error, mantener un array vacío
      setUsuarios([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Obtener usuario por ID
  const fetchUsuarioById = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await usuarioApi.getById(id);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error al cargar usuario';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Crear nuevo usuario
  const createUsuario = async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await usuarioApi.create(userData);
      toast.success('Usuario creado exitosamente');
      
      // Actualizar la lista local
      setUsuarios(prev => [response.data.user, ...prev]);
      
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error al crear usuario';
      setError(errorMessage);
      
      // Manejar errores de validación
      if (error.response?.data?.error && typeof error.response.data.error === 'object') {
        Object.values(error.response.data.error).forEach(errors => {
          errors.forEach(errorMsg => toast.error(errorMsg));
        });
      } else {
        toast.error(errorMessage);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Actualizar usuario
  const updateUsuario = async (id, userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await usuarioApi.update(id, userData);
      toast.success('Usuario actualizado exitosamente');
      
      // Actualizar la lista local
      setUsuarios(prev => 
        prev.map(usuario => 
          usuario.id === id ? response.data : usuario
        )
      );
      
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error al actualizar usuario';
      setError(errorMessage);
      
      // Manejar errores de validación
      if (error.response?.data?.error && typeof error.response.data.error === 'object') {
        Object.values(error.response.data.error).forEach(errors => {
          errors.forEach(errorMsg => toast.error(errorMsg));
        });
      } else {
        toast.error(errorMessage);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Eliminar usuario
  const deleteUsuario = async (id) => {
    setLoading(true);
    setError(null);
    try {
      await usuarioApi.delete(id);
      toast.success('Usuario eliminado exitosamente');
      
      // Actualizar la lista local
      setUsuarios(prev => prev.filter(usuario => usuario.id !== id));
      
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error al eliminar usuario';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Obtener proyectos de un usuario
  const fetchProyectosByUsuario = async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await usuarioApi.getProyectos(userId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error al cargar proyectos del usuario';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    usuarios,
    loading,
    error,
    fetchUsuarios,
    fetchUsuarioById,
    createUsuario,
    updateUsuario,
    deleteUsuario,
    fetchProyectosByUsuario,
    setUsuarios
  };
};

export default useUsuarios;
